import React, { useState } from "react";
import Button from "../../../../components/ui/Button";
import AdminDataTable from "../AdminDataTable";
import { Plus, Eye, Edit, Trash2 } from "lucide-react";
import { useData } from "../../../../context/DataContext";
import AdminCreateAdModal from "./AdminCreateAdModal";

const AdManager = () => {
  const { adCampaigns, addAdCampaign } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const adColumns = [
    { 
      header: "Campaign Name", 
      key: "name", 
      render: (r) => (
        <div>
          <span className="font-bold text-slate-800">{r.name}</span>
          <p className="text-xs text-slate-500">{r.client || r.advertiser}</p>
        </div>
      ) 
    },
    { 
      header: "Target Creator", 
      key: "targetCreator",
      render: (r) => (
        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-lg border border-purple-100 font-medium text-xs">
          {r.targetCreator || "Unassigned"}
        </span>
      )
    },
    { 
      header: "Performance", 
      key: "performance", 
      render: (r) => (
        <div className="space-y-1">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500">Budget:</span>
            <span className="font-semibold text-blue-600">{r.budget} ETB</span>
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500">Duration:</span>
            <span className="font-semibold text-slate-700">{r.duration || 30} Days</span>
          </div>
        </div>
      )
    },
    { 
      header: "Status", 
      key: "status", 
      render: (r) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
          r.status === "Active" 
            ? "bg-emerald-100 text-emerald-700" 
            : "bg-slate-100 text-slate-500"
        }`}>
          {r.status}
        </span>
      ) 
    },
    { 
      header: "Actions", 
      key: "actions", 
      render: (r) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50">
            <Edit size={16} onClick={() => {
              setEditingCampaign(r);
              setIsModalOpen(true);
            }} />
          </Button>
          <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50">
            <Trash2 size={16} />
          </Button>
        </div>
      ) 
    }
  ];

  const handleCreateOrUpdate = (data) => {
    // If we're editing, we'd normally update the context, 
    // but for the prototype addAdCampaign handles the list.
    addAdCampaign(data);
    setIsModalOpen(false);
    setEditingCampaign(null);
  };

  const totalBudget = adCampaigns.reduce((acc, curr) => acc + parseInt((curr.budget || "0").replace(/,/g, '')), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-slate-700">Ad Campaigns</h3>
        <Button onClick={() => setIsModalOpen(true)}><Plus className="w-4 h-4 mr-2" /> New Campaign</Button>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
           <div className="text-sm text-slate-500 mb-1">Total Active Budgets</div>
           <div className="text-2xl font-bold text-slate-800">{totalBudget.toLocaleString()} ETB</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
           <div className="text-sm text-slate-500 mb-1">Participating Creators</div>
           <div className="text-2xl font-bold text-purple-600">{adCampaigns.reduce((acc, c) => acc + (c.creators || 0), 0)}</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
           <div className="text-sm text-slate-500 mb-1">Avg Engagement</div>
           <div className="text-2xl font-bold text-emerald-600">4.8%</div>
        </div>
      </div>
      <AdminDataTable 
        columns={adColumns.map(c => ({...c, sortable: true}))} 
        data={adCampaigns}
        searchableFields={["name", "client", "status"]}
        filterableFields={[
            { key: "status", label: "Status", options: ["Active", "Scheduled", "Completed"] }
        ]} 
      />
      
      <AdminCreateAdModal 
        isOpen={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          setEditingCampaign(null);
        }} 
        campaignToEdit={editingCampaign}
        onSave={handleCreateOrUpdate}
      />
    </div>
  );
};

export default AdManager;
